import type { BackendApiPort } from './ports/BackendApiPort';
import type { MeetingSocketPort } from './ports/MeetingSocketPort';
import { HttpBackendApiClient } from './adapters/HttpBackendApiClient';
import { WsMeetingSocket } from './adapters/WsMeetingSocket';

const DEFAULT_BASE_URL = import.meta.env.VITE_BACKEND_API_URL ?? 'http://localhost:3001/api/v1';

let backendApiInstance: BackendApiPort | null = null;
let meetingSocketInstance: MeetingSocketPort | null = null;

/** In-memory holder for the access token, injected into HttpBackendApiClient.
 * Kept private to this container — callers set it via setAccessToken(), never
 * by reaching into the adapter directly. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasAccessToken(): boolean {
  return accessToken !== null && accessToken !== '';
}

/**
 * Returns the singleton BackendApiPort. Swap HttpBackendApiClient for a
 * different transport (e.g. a generated SDK) here only — no other file
 * needs to change.
 */
export function getBackendApiClient(): BackendApiPort {
  if (!backendApiInstance) {
    backendApiInstance = new HttpBackendApiClient({
      baseUrl: DEFAULT_BASE_URL,
      getAccessToken: () => accessToken,
    });
  }
  return backendApiInstance;
}

/**
 * Returns the singleton MeetingSocketPort. A single socket instance is
 * shared app-wide — the store/hook consuming it is responsible for calling
 * connect()/disconnect() around the active meeting's lifecycle.
 */
export function getMeetingSocket(): MeetingSocketPort {
  if (!meetingSocketInstance) {
    meetingSocketInstance = new WsMeetingSocket({ baseUrl: DEFAULT_BASE_URL });
  }
  return meetingSocketInstance;
}

/** Test/mock-only escape hatch — never call from application code. */
export function __resetBackendApiContainer(): void {
  backendApiInstance = null;
  meetingSocketInstance = null;
  accessToken = null;
}